import { useState, useEffect, useCallback } from 'react';
import { Project, ProjectDetailsParams } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { captureException } from '../utils/captureException';

interface UseProjectResult {
  project: Project | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  setProject: (project: Project) => void;
}

export const useProject = (params: ProjectDetailsParams): UseProjectResult => {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${process.env.REACT_APP_API_URL || ''}/api/project/${encodeURIComponent(params.project_code)}`;
      const urlWarmup = `${process.env.REACT_APP_API_URL || ''}/api/warm_up`;
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }

      fetch(urlWarmup, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }).then((response) => {
        // console.log("Warmup response:", response);
      }).catch((error) => {
        captureException(error, { tags: { feature: 'useProject' }, extra: { action: 'warmup', project_code: params.project_code } });
        console.error('Error warming up endpoints:', error);
      });

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("project response", data);
      // Verify that data is not empty
      if (Object.keys(data).length === 0) {
        throw new Error('No data found');
      }
      const sortedKeys = Object.keys(data).sort();
      for (const key of sortedKeys) {
        if (key.startsWith("DETALLES") && !key.includes("DE LA ORDEN") && data[key] != null) {
          // console.log(data[key]);
          Object.entries(data[key]).forEach(([element, value]) => {
            if (typeof (value as any)?.value === 'object' && (value as any)?.value !== null) {
              // console.log("element", element, "value", value);
              let values = "";
              Object.entries((value as any)?.value || {}).map(([key, value]) => (
                values += key + ": " + value + "\n"
              ));
              data[key][element]["value"] = values;
            }
          });
        }
      }
      // console.log("data", data);
      setProject(data as Project);
      
    } catch (err) {
      captureException(err, { tags: { feature: 'useProject' }, extra: { action: 'fetchProject', project_code: params.project_code } });
      console.error('Error fetching project:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al cargar proyectos');
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [params.project_code]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return {
    project,
    loading,
    error,
    refetch: fetchProject,
    setProject,
  };
};
